import React, { useEffect, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { FontIcon } from '@arvoreeducacao/bonsai-ds'
import { useFloraTooltipStore } from 'stores/floraTooltipStore'
import {
  getActiveTooltipTrigger,
  buildFloraChatUrl
} from './flora-tooltip-triggers'
import { useTranslation } from 'i18n'
import type { LanguageTokens } from 'i18n'

type FloraRouteTooltipProps = {
  children: React.ReactNode
}

const TOOLTIP_GRADIENT = 'linear-gradient(90deg, #5CDDCF 0%, #8BB8E8 100%)'

const TOOLTIP_SHADOW =
  '-7px 8px 22.1px 0px rgba(92, 221, 207, 0.23), 9px 0px 15.6px 0px rgba(65, 166, 236, 0.17)'

const FloraRouteTooltip: React.FC<FloraRouteTooltipProps> = ({ children }) => {
  const history = useHistory()
  const { t } = useTranslation()
  const { seenTooltips, markTooltipSeen } = useFloraTooltipStore()
  const [activeTrigger, setActiveTrigger] =
    useState<ReturnType<typeof getActiveTooltipTrigger>>(null)
  const [isVisible, setIsVisible] = useState(false)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [tooltipWidth, setTooltipWidth] = useState(0)
  const [displayedText, setDisplayedText] = useState('')

  useEffect(() => {
    if (tooltipRef.current && activeTrigger) {
      setTooltipWidth(tooltipRef.current.offsetWidth)
    }
  }, [activeTrigger, isVisible, displayedText])

  useEffect(() => {
    if (!activeTrigger || !isVisible) {
      setDisplayedText('')
      return
    }

    const fullText = t(activeTrigger.tooltipText as LanguageTokens)
    let index = 0
    setDisplayedText('')

    const interval = setInterval(() => {
      index++
      setDisplayedText(fullText.slice(0, index))
      if (index >= fullText.length) {
        clearInterval(interval)
      }
    }, 30)

    return () => clearInterval(interval)
  }, [activeTrigger, isVisible, t])

  useEffect(() => {
    const checkRoute = () => {
      const pathname = window.location.pathname
      const hash = window.location.hash
      const trigger = getActiveTooltipTrigger(pathname, hash)

      if (trigger && !seenTooltips[trigger.id]) {
        setActiveTrigger(trigger)
        requestAnimationFrame(() => setIsVisible(true))
      } else {
        setIsVisible(false)
        setActiveTrigger(null)
      }
    }

    checkRoute()

    const unlisten = history.listen(() => checkRoute())
    const handleHashChange = () => checkRoute()
    window.addEventListener('hashchange', handleHashChange)

    return () => {
      unlisten()
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [history, seenTooltips])

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (activeTrigger) {
      markTooltipSeen(activeTrigger.id)
      setIsVisible(false)
      setActiveTrigger(null)
    }
  }

  const handleTooltipClick = () => {
    if (activeTrigger) {
      markTooltipSeen(activeTrigger.id)
      window.location.href = buildFloraChatUrl(
        t(activeTrigger.chatMessage as LanguageTokens)
      )
    }
  }

  const handleFloraClick = () => {
    if (activeTrigger) {
      markTooltipSeen(activeTrigger.id)
      window.location.href = buildFloraChatUrl(
        t(activeTrigger.chatMessage as LanguageTokens)
      )
    } else {
      window.location.href = '/app-v2/arvore-intelligence'
    }
  }

  const arrowSize = 28
  const buttonWidth = 40
  const tooltipOffset = tooltipWidth ? tooltipWidth / 2 - buttonWidth / 2 : 0

  return (
    <div className="relative flex items-center">
      <button
        id="flora-ai-button"
        className="flex items-center justify-center ml-2 w-10 h-10 rounded-full cursor-pointer hover:opacity-80 transition-opacity relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 90% -36%, rgba(169, 94, 244, 1) 32%, rgba(66, 125, 255, 1) 41%, rgba(41, 210, 192, 1) 83%, rgba(31, 215, 195, 1) 93%)',
          boxShadow:
            'inset 0px 2.76px 10.83px 0.69px rgba(92, 255, 237, 0.39), inset 0px -0.69px 17.39px 0px rgba(255, 255, 255, 0)'
        }}
        onClick={handleFloraClick}
        aria-label="Flora AI"
      >
        {children}
      </button>

      {activeTrigger && isVisible && (
        <div
          ref={tooltipRef}
          className="absolute z-50 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2"
          style={{
            top: 'calc(100% + 36px)',
            right: `-${tooltipOffset}px`,
            animationDuration: '200ms',
            animationFillMode: 'both'
          }}
        >
          <div className="relative">
            <div
              style={{
                position: 'absolute',
                width: `${arrowSize}px`,
                height: `${arrowSize}px`,
                left: '50%',
                top: `-${arrowSize / 2 - 2}px`,
                background: TOOLTIP_GRADIENT,
                transform: 'translateX(-50%) rotate(45deg)',
                borderRadius: '5px',
                zIndex: -1
              }}
            />
            <div
              className="flex items-center gap-1.5 pl-5 pr-2.5 rounded-[30px] cursor-pointer hover:opacity-95 transition-opacity"
              style={{
                background: TOOLTIP_GRADIENT,
                boxShadow: TOOLTIP_SHADOW,
                height: '52px'
              }}
              onClick={handleTooltipClick}
            >
              <p className="text-[#053B4B] text-base font-bold leading-[1.3] whitespace-nowrap relative">
                {displayedText}
              </p>
              <button
                className="flex items-center justify-center shrink-0 p-2 cursor-pointer transition-opacity hover:opacity-70 relative text-[#053B4B]"
                onClick={handleDismiss}
                aria-label={t('Fechar tooltip')}
              >
                <FontIcon iconName="cancel" size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FloraRouteTooltip
